/*
 * Client-side wishlist, mirroring the legacy guest flow: handles are kept in
 * localStorage (no account required), hearts toggle instantly, and the header
 * badge stays in sync across the page and across tabs.
 *
 * Public API: window.MSWishlist { list(), has(handle), toggle(handle), count() }
 * Events: document "ms:wishlist:change" { detail: { handles } }
 * Markup contract:
 *   [data-ms-wishlist-toggle][data-handle] — heart buttons (aria-pressed managed)
 *   [data-ms-wishlist-count]               — badge elements ([hidden] when 0)
 */
(() => {
  'use strict';

  const KEY = 'ms_wishlist_v1';

  const read = () => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(KEY) || '[]');
      return Array.isArray(parsed) ? parsed.filter((h) => typeof h === 'string' && h) : [];
    } catch (error) {
      return [];
    }
  };

  const write = (handles) => {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(handles));
    } catch (error) {
      /* storage full / private mode — keep the in-memory state */
    }
  };

  let handles = read();

  const render = () => {
    document.querySelectorAll('[data-ms-wishlist-count]').forEach((el) => {
      el.textContent = String(handles.length);
      el.hidden = handles.length === 0;
    });
    document.querySelectorAll('[data-ms-wishlist-toggle]').forEach((btn) => {
      const active = handles.includes(btn.dataset.handle);
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', String(active));
    });
  };

  const notify = () => {
    render();
    document.dispatchEvent(new CustomEvent('ms:wishlist:change', { detail: { handles: [...handles] } }));
  };

  window.MSWishlist = {
    list: () => [...handles],
    count: () => handles.length,
    has: (handle) => handles.includes(handle),
    toggle(handle) {
      if (!handle) return false;
      const index = handles.indexOf(handle);
      if (index === -1) handles.unshift(handle);
      else handles.splice(index, 1);
      write(handles);
      notify();
      return handles.includes(handle);
    },
    remove(handle) {
      const index = handles.indexOf(handle);
      if (index !== -1) {
        handles.splice(index, 1);
        write(handles);
        notify();
      }
    },
    // Re-applies badge/heart state; call after injecting HTML with hearts.
    sync: () => render(),
  };

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-ms-wishlist-toggle]');
    if (!btn) return;
    event.preventDefault();
    event.stopPropagation();
    window.MSWishlist.toggle(btn.dataset.handle);
  });

  // Another tab changed the list — pick it up.
  window.addEventListener('storage', (event) => {
    if (event.key !== KEY) return;
    handles = read();
    notify();
  });

  // Back/forward-cache restore: localStorage may have changed on other pages.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    handles = read();
    render();
  });

  // Hearts injected after load (fetched recommendations, client-rendered
  // grids) start unstyled; re-sync whenever new toggles appear.
  let syncQueued = false;
  const queueSync = () => {
    if (syncQueued) return;
    syncQueued = true;
    window.requestAnimationFrame(() => {
      syncQueued = false;
      render();
    });
  };
  const startObserver = () => {
    if (!document.body || !('MutationObserver' in window)) return;
    new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches?.('[data-ms-wishlist-toggle], [data-ms-wishlist-count]') ||
              node.querySelector?.('[data-ms-wishlist-toggle], [data-ms-wishlist-count]')) {
            queueSync();
            return;
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      render();
      startObserver();
    });
  } else {
    render();
    startObserver();
  }
})();
